import { ForwardedRef } from 'react'

import { useMutation } from '@apollo/client'
import classNames from 'classnames'
import { ElementOf } from 'graphql-config'

import { openErrorToast } from '@web/components/Toast/GlobalToastPortal'
import { gql } from '@web/gql'
import { ReactionLayer_ReviewGroupFragment, ReviewGroupReactionSort, ReactionAction } from '@web/gql/graphql'
import { useGoTo } from '@web/hooks/useGoTo'
import useMe from '@web/hooks/useMe'
import { extractByTypename } from '@web/libs/apollo/typeGuard'
import { QueryKeySpecifier } from '@web/libs/apollo/typePolicies.helper'
import { messages } from '@web/libs/constants'

import { ORDERED_REACTION_TYPES, ReactionLottieType, ReactionNClickArea } from '../reaction.constant'
import styles from './ReactionLayer.module.scss'
import ReactionLottie from '../ReactionLottie/ReactionLottie'

const ReactToReviewGroupMutation = gql(`
  mutation ReactToReviewGroup($input: ReactReviewGroupInput!) {
    reactToReviewGroup(input: $input) {
      ... on ReactReviewGroupSucceed {
        __typename
        reactedReviewGroup {
          id
          ...ReactionArea_ReviewGroup
        }
      }
      ... on BaseError {
        __typename
        message
      }
    }
  }
`)

type Props = {
  reviewGroup: ReactionLayer_ReviewGroupFragment
  closeReactionLayer: (...args: any[]) => void
  nClickArea: ReactionNClickArea
  showOnTop?: boolean
  layerRef?: ForwardedRef<HTMLDivElement>
}

export const ReactionLayer = ({ reviewGroup, closeReactionLayer, nClickArea, showOnTop, layerRef }: Props) => {
  const { goToLogin } = useGoTo()
  const { login } = useMe()

  const handleNetworkError = () => openErrorToast(messages.ERR_MUTATION)

  const handleAuthenticationError = () => {
    if (window.confirm(messages.ERR_LOGIN)) goToLogin()
  }

  const [reactToReviewGroup, { loading }] = useMutation(ReactToReviewGroupMutation, {
    onCompleted: data => {
      const { AuthenticationError, ReactReviewGroupSucceed } = extractByTypename(data.reactToReviewGroup)

      if (AuthenticationError) {
        handleAuthenticationError()
        return
      }

      if (!ReactReviewGroupSucceed) {
        handleNetworkError()
        return
      }
    },
    onError: handleNetworkError,
    update: (cache, res) => {
      const { ReactReviewGroupSucceed } = extractByTypename(res.data?.reactToReviewGroup)
      if (!ReactReviewGroupSucceed) return

      const REVIEWGROUP_REACTIONS_QUERYKEY: ElementOf<QueryKeySpecifier> = 'reviewGroupReactions'

      const oldReactionType = reviewGroup.myReactionType
      const newReactionType = ReactReviewGroupSucceed.reactedReviewGroup.myReactionType

      cache.evict({
        fieldName: REVIEWGROUP_REACTIONS_QUERYKEY,
        args: {
          input: {
            reviewGroupId: reviewGroup.id,
            sort: ReviewGroupReactionSort.CreatedDateTimeDesc,
            reactionType: oldReactionType,
          },
        },
      })

      if (newReactionType)
        cache.evict({
          fieldName: REVIEWGROUP_REACTIONS_QUERYKEY,
          args: {
            input: {
              reviewGroupId: reviewGroup.id,
              sort: ReviewGroupReactionSort.CreatedDateTimeDesc,
              reactionType: newReactionType,
            },
          },
        })
    },
  })

  const cancelReaction = async () => {
    if (!reviewGroup.myReactionType) return

    await reactToReviewGroup({
      variables: {
        input: {
          actionType: ReactionAction.Cancel,
          reviewGroupId: reviewGroup.id,
          reactionType: reviewGroup.myReactionType,
        },
      },
    })
  }

  const updateReaction = async (reactionTypename: string) => {
    await reactToReviewGroup({
      variables: {
        input: {
          actionType: ReactionAction.Update,
          reviewGroupId: reviewGroup.id,
          reactionType: reactionTypename,
        },
      },
    })
  }

  const toggleUpdateReaction = async (reactionTypename: string) => {
    if (!login) {
      handleAuthenticationError()
      return
    }

    if (loading) {
      openErrorToast(messages.WAIT_REQUEST)
      return
    }

    const isReactionClickedAgain = reviewGroup.myReactionType === reactionTypename
    if (isReactionClickedAgain) {
      await cancelReaction()
      return
    }

    await updateReaction(reactionTypename)
    return
  }

  const nClickCodeMap: Record<ReactionLottieType, string> = {
    helpful: nClickArea.HELPFUL,
    fun: nClickArea.FUN,
    cool: nClickArea.COOL,
    wannago: nClickArea.WANNAGO,
  }

  return (
    <div className={classNames(styles.ly_reaction, { [styles.type_show_top]: showOnTop })} ref={layerRef}>
      {ORDERED_REACTION_TYPES.map(reactionType => {
        return (
          <ReactionLottie
            key={reactionType}
            reactionType={reactionType}
            myReactionType={reviewGroup.myReactionType}
            updateReaction={toggleUpdateReaction}
            closeReactionLayer={closeReactionLayer}
            nClickCode={nClickCodeMap[reactionType]}
          />
        )
      })}
    </div>
  )
}

ReactionLayer.fragments = gql(`
  fragment ReactionLayer_ReviewGroup on ReviewGroup {
    id
    myReactionType
  }
`)
